import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import {
  BsArrowClockwise,
  BsArrowUpRight,
  BsCheck2,
  BsClipboard,
  BsEnvelope,
  BsExclamationCircle,
} from "react-icons/bs";
import { useLocation, useNavigate } from "react-router-dom";
import {
  buildGmailComposeUrl,
  contactEmail,
  contactGmailComposeUrl,
} from "../../data/siteMeta";
import {
  getProjectFocusLabel,
  normalizeProjectFocus,
  projectFocusOptions,
} from "../../data/projectData";

const briefStorageKey = "portfolio-project-brief";

const inquiryTypes = [
  "Website build or redesign",
  "Web app, client portal, or dashboard",
  "Website or web app with supporting integration",
  "UI refresh or frontend polish",
  "Unsure yet",
];

const persistedBriefFields = ["projectType", "summary", "link"];

const initialBrief = {
  projectType: "",
  summary: "",
  link: "",
  sourceProject: "",
  sourceFocus: "",
};

function normalizeInquiryType(projectType) {
  const legacyInquiryMap = {
    "Full product build or revamp": "Web app, client portal, or dashboard",
    "Internal tool or workflow system": "Web app, client portal, or dashboard",
    "Web app, dashboard, or portal": "Web app, client portal, or dashboard",
    "Workflow integration or smart feature": "Website or web app with supporting integration",
    "AI-assisted feature or automation": "Website or web app with supporting integration",
    "API or backend implementation": "Website or web app with supporting integration",
    "Automation or AI integration": "Website or web app with supporting integration",
    "Website or web app with smart integration": "Website or web app with supporting integration",
  };

  return legacyInquiryMap[projectType] || projectType;
}

function readSavedBrief() {
  if (typeof window === "undefined") {
    return { ...initialBrief };
  }

  try {
    const storedBrief = window.localStorage.getItem(briefStorageKey);

    if (!storedBrief) {
      return { ...initialBrief };
    }

    const parsedBrief = JSON.parse(storedBrief);

    return persistedBriefFields.reduce(
      (nextBrief, fieldName) => ({
        ...nextBrief,
        [fieldName]: typeof parsedBrief?.[fieldName] === "string"
          ? fieldName === "projectType"
            ? normalizeInquiryType(parsedBrief[fieldName])
            : parsedBrief[fieldName]
          : "",
      }),
      { ...initialBrief }
    );
  } catch {
    return { ...initialBrief };
  }
}

function persistBrief(brief) {
  if (typeof window === "undefined") {
    return;
  }

  const briefToPersist = persistedBriefFields.reduce(
    (nextBrief, fieldName) => ({
      ...nextBrief,
      [fieldName]: brief[fieldName],
    }),
    {}
  );

  const hasContent = Object.values(briefToPersist).some(
    (value) => value.trim() !== ""
  );

  try {
    if (!hasContent) {
      window.localStorage.removeItem(briefStorageKey);
      return;
    }

    window.localStorage.setItem(briefStorageKey, JSON.stringify(briefToPersist));
  } catch {
    // Ignore storage failures and keep the form usable.
  }
}

function buildProjectBriefBody(brief) {
  return [
    "Hi Om,",
    "",
    "Here is a short inquiry:",
    "",
    `Project type: ${brief.projectType || "-"}`,
    `Browsing focus: ${brief.sourceFocus || "-"}`,
    `Reference project: ${brief.sourceProject || "-"}`,
    `What needs help: ${brief.summary || "-"}`,
    `Reference link: ${brief.link || "-"}`,
    "",
    "Thanks,",
  ].join("\n");
}

function buildProjectGmailUrl(brief) {
  const subjectContext =
    brief.projectType ||
    brief.sourceProject ||
    "New project inquiry";
  const subject = `Project Inquiry - ${subjectContext}`;
  const body = buildProjectBriefBody(brief);
  return buildGmailComposeUrl({
    subject,
    body,
  });
}

function validateBrief(brief) {
  const nextErrors = {};

  if (!brief.projectType) {
    nextErrors.projectType = "Choose the kind of project you want help with.";
  }

  if (!brief.summary.trim()) {
    nextErrors.summary = "Describe the page, workflow, or product friction.";
  }

  return nextErrors;
}

function fallbackCopyText(text) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  const didCopy = document.execCommand("copy");
  document.body.removeChild(textarea);

  if (!didCopy) {
    throw new Error("Clipboard copy did not complete.");
  }
}

async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await Promise.race([
        navigator.clipboard.writeText(text),
        new Promise((_, reject) => {
          window.setTimeout(() => {
            reject(new Error("Clipboard write timed out."));
          }, 900);
        }),
      ]);
      return;
    } catch {
      fallbackCopyText(text);
      return;
    }
  }

  fallbackCopyText(text);
}

function ProjectBriefForm() {
  const { hash, pathname, search } = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(search);
  const requestedProjectType = searchParams.get("projectType");
  const rawRequestedFocusId = searchParams.get("focus")?.trim() || "";
  const requestedSourceProject = searchParams.get("sourceProject")?.trim() || "";
  const normalizedRequestedProjectType = normalizeInquiryType(requestedProjectType);
  const normalizedRequestedFocusId = normalizeProjectFocus(rawRequestedFocusId);
  const prefilledProjectType = inquiryTypes.includes(normalizedRequestedProjectType)
    ? normalizedRequestedProjectType
    : "";
  const prefilledSourceProject = requestedSourceProject;
  const prefilledSourceFocus = projectFocusOptions.some(
    (option) => option.id === normalizedRequestedFocusId && option.id !== "all"
  )
    ? getProjectFocusLabel(normalizedRequestedFocusId)
    : "";
  const resetBriefState = {
    ...initialBrief,
    projectType: prefilledProjectType,
    sourceProject: prefilledSourceProject,
    sourceFocus: prefilledSourceFocus,
  };
  const [brief, setBrief] = useState(() => ({
    ...readSavedBrief(),
    ...(prefilledProjectType ? { projectType: prefilledProjectType } : {}),
    sourceProject: prefilledSourceProject,
    sourceFocus: prefilledSourceFocus,
  }));
  const [showOptionalFields, setShowOptionalFields] = useState(() =>
    Boolean(readSavedBrief().link)
  );
  const [errors, setErrors] = useState({});
  const [copyState, setCopyState] = useState("idle");
  const contextItems = [
    prefilledProjectType
      ? { label: "Project type", value: prefilledProjectType }
      : null,
    brief.sourceFocus
      ? { label: "Browsing focus", value: brief.sourceFocus }
      : null,
    brief.sourceProject
      ? { label: "Reference project", value: brief.sourceProject }
      : null,
  ].filter(Boolean);
  const hasCustomDraft = Object.keys(resetBriefState).some(
    (fieldName) => brief[fieldName] !== resetBriefState[fieldName]
  );
  const isBriefCopied = copyState === "brief-copied";
  const isEmailCopied = copyState === "email-copied";
  const hasBriefCopyError = copyState === "brief-error";
  const hasEmailCopyError = copyState === "email-error";

  useEffect(() => {
    let shouldReplace = false;
    const nextParams = new URLSearchParams(search);

    if (
      requestedProjectType &&
      normalizedRequestedProjectType &&
      normalizedRequestedProjectType !== requestedProjectType
    ) {
      nextParams.set("projectType", normalizedRequestedProjectType);
      shouldReplace = true;
    }

    if (
      rawRequestedFocusId &&
      normalizedRequestedFocusId &&
      normalizedRequestedFocusId !== rawRequestedFocusId
    ) {
      nextParams.set("focus", normalizedRequestedFocusId);
      shouldReplace = true;
    }

    if (!shouldReplace) {
      return;
    }

    navigate(
      {
        pathname,
        search: `?${nextParams.toString()}`,
        hash,
      },
      { replace: true }
    );
  }, [
    hash,
    navigate,
    normalizedRequestedFocusId,
    normalizedRequestedProjectType,
    pathname,
    rawRequestedFocusId,
    requestedProjectType,
    search,
  ]);

  useEffect(() => {
    setBrief((currentBrief) => {
      let hasChanged = false;
      const nextBrief = { ...currentBrief };

      if (
        prefilledProjectType &&
        currentBrief.projectType !== prefilledProjectType
      ) {
        nextBrief.projectType = prefilledProjectType;
        hasChanged = true;
      }

      if (currentBrief.sourceProject !== prefilledSourceProject) {
        nextBrief.sourceProject = prefilledSourceProject;
        hasChanged = true;
      }

      if (currentBrief.sourceFocus !== prefilledSourceFocus) {
        nextBrief.sourceFocus = prefilledSourceFocus;
        hasChanged = true;
      }

      if (!hasChanged) {
        return currentBrief;
      }

      return nextBrief;
    });

    if (prefilledProjectType) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        projectType: "",
      }));
    }
  }, [prefilledProjectType, prefilledSourceProject, prefilledSourceFocus]);

  useEffect(() => {
    persistBrief(brief);
  }, [brief]);

  useEffect(() => {
    if (brief.link && !showOptionalFields) {
      setShowOptionalFields(true);
    }
  }, [brief.link, showOptionalFields]);

  useEffect(() => {
    if (copyState === "idle") {
      return undefined;
    }

    const resetTimer = window.setTimeout(() => {
      setCopyState("idle");
    }, 2400);

    return () => {
      window.clearTimeout(resetTimer);
    };
  }, [copyState]);

  const updateBriefField = (fieldName, value) => {
    setBrief((currentBrief) => ({
      ...currentBrief,
      [fieldName]: value,
    }));

    if (errors[fieldName]) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        [fieldName]: "",
      }));
    }

    if (copyState !== "idle") {
      setCopyState("idle");
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    updateBriefField(name, value);
  };

  const handleProjectTypeSelect = (projectType) => {
    updateBriefField("projectType", projectType);
  };

  const handleClearDraft = () => {
    setBrief(resetBriefState);
    setShowOptionalFields(false);
    setErrors({});
    setCopyState("idle");
  };

  const handleOpenGmail = () => {
    const nextErrors = validateBrief(brief);

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    window.location.href = buildProjectGmailUrl(brief);
  };

  const handleCopyBrief = async () => {
    const nextErrors = validateBrief(brief);

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setCopyState("idle");
      return;
    }

    try {
      await copyText(buildProjectBriefBody(brief));
      setCopyState("brief-copied");
    } catch {
      setCopyState("brief-error");
    }
  };

  const handleCopyEmail = async () => {
    try {
      await copyText(contactEmail);
      setCopyState("email-copied");
    } catch {
      setCopyState("email-error");
    }
  };

  return (
    <Form className="project-brief-form project-brief-form-compact" noValidate>
      {contextItems.length > 0 ? (
        <div className="project-brief-context-strip">
          <span className="project-mini-heading">Inquiry context</span>
          <div className="project-brief-context-pills">
            {contextItems.map((item) => (
              <span key={item.label} className="project-brief-context-pill">
                <strong>{item.label}:</strong> {item.value}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <div className="brief-direct-bar">
        <div className="brief-direct-copy">
          <span className="project-mini-heading">Fastest route</span>
          <h4>{contactEmail}</h4>
          <p>Already know the scope? Open a Gmail draft or copy the address.</p>
        </div>

        <div className="brief-direct-actions">
          <Button
            type="button"
            variant="outline-light"
            className={`brief-direct-button action-feedback-btn${isEmailCopied ? " is-success" : ""}${hasEmailCopyError ? " is-error" : ""}`}
            onClick={handleCopyEmail}
          >
            {isEmailCopied ? <BsCheck2 aria-hidden="true" /> : <BsEnvelope aria-hidden="true" />}
            <span>{isEmailCopied ? "Email copied" : hasEmailCopyError ? "Copy failed" : "Copy email"}</span>
          </Button>
          <a
            className="contact-link project-brief-direct-link"
            href={contactGmailComposeUrl}
          >
            <BsArrowUpRight aria-hidden="true" />
            <span>Open Gmail</span>
          </a>
        </div>
      </div>

      <fieldset
        className="brief-type-group"
        aria-describedby={errors.projectType ? "project-brief-type-error" : "project-brief-note"}
      >
        <legend>Project type</legend>
        <div className="brief-type-chip-row">
          {inquiryTypes.map((type) => (
            <button
              key={type}
              type="button"
              className={`brief-type-chip${brief.projectType === type ? " is-active" : ""}`}
              onClick={() => handleProjectTypeSelect(type)}
              aria-pressed={brief.projectType === type}
            >
              {type}
            </button>
          ))}
        </div>
        {errors.projectType ? (
          <p className="brief-error" id="project-brief-type-error" role="alert">
            {errors.projectType}
          </p>
        ) : null}
      </fieldset>

      <Form.Group
        className="brief-field brief-field-full"
        controlId="project-brief-summary"
      >
        <Form.Label>What needs help</Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          className="brief-input brief-textarea"
          name="summary"
          placeholder="What page, product, or workflow needs help first?"
          value={brief.summary}
          onChange={handleChange}
          required
          aria-invalid={Boolean(errors.summary)}
          aria-describedby={errors.summary ? "project-brief-summary-error project-brief-note" : "project-brief-note"}
        />
        {errors.summary ? (
          <p className="brief-error" id="project-brief-summary-error" role="alert">
            {errors.summary}
          </p>
        ) : null}
      </Form.Group>

      <div className="project-brief-optional">
        <button
          type="button"
          className="brief-optional-toggle"
          aria-expanded={showOptionalFields}
          onClick={() => setShowOptionalFields((currentValue) => !currentValue)}
        >
          {showOptionalFields ? "Hide reference link" : "Add a reference link (optional)"}
        </button>

        {showOptionalFields ? (
          <Form.Group className="brief-field brief-field-full" controlId="project-brief-link">
            <Form.Label>Reference link</Form.Label>
            <Form.Control
              className="brief-input"
              name="link"
              type="url"
              placeholder="Site, app, screenshots, Loom, or repo link"
              value={brief.link}
              onChange={handleChange}
              inputMode="url"
              autoComplete="url"
            />
          </Form.Group>
        ) : null}
      </div>

      <div className="project-brief-actions project-brief-actions-compact">
        <Button
          type="button"
          className={`action-feedback-btn${isBriefCopied ? " is-success" : ""}${hasBriefCopyError ? " is-error" : ""}`}
          onClick={handleCopyBrief}
        >
          {isBriefCopied ? <BsCheck2 aria-hidden="true" /> : <BsClipboard aria-hidden="true" />}
          <span>{isBriefCopied ? "Inquiry copied" : hasBriefCopyError ? "Copy failed" : "Copy inquiry"}</span>
        </Button>
        <Button type="button" variant="outline-light" onClick={handleOpenGmail}>
          <BsArrowUpRight aria-hidden="true" />
          <span>Open in Gmail</span>
        </Button>
        {hasCustomDraft ? (
          <Button
            type="button"
            variant="outline-light"
            className="project-brief-reset-btn"
            onClick={handleClearDraft}
          >
            <BsArrowClockwise aria-hidden="true" />
            <span>Clear</span>
          </Button>
        ) : null}
      </div>

      <p className="project-brief-note" id="project-brief-note">
        Open in Gmail creates a prefilled draft. Copy inquiry still works if
        you want to send it from another inbox.
      </p>

      {isBriefCopied ? (
        <p className="project-brief-status is-success" role="status" aria-live="polite">
          <BsCheck2 className="project-brief-status-icon" aria-hidden="true" />
          <span>
          Inquiry copied. Paste it into an email to {contactEmail}.
          </span>
        </p>
      ) : null}

      {isEmailCopied ? (
        <p className="project-brief-status is-success" role="status" aria-live="polite">
          <BsCheck2 className="project-brief-status-icon" aria-hidden="true" />
          <span>
          Email address copied. Send your note to {contactEmail}.
          </span>
        </p>
      ) : null}

      {hasBriefCopyError || hasEmailCopyError ? (
        <p className="project-brief-status is-error" role="alert">
          <BsExclamationCircle className="project-brief-status-icon" aria-hidden="true" />
          <span>
          Copy did not complete in this browser. You can still email {contactEmail} directly.
          </span>
        </p>
      ) : null}
    </Form>
  );
}

export default ProjectBriefForm;
